import { ILegacyCartEntry, IPrices } from '@rbi-ctg/menu';
import { IRestaurant } from '@rbi-ctg/store';
import { ServiceMode } from 'generated/graphql-gateway';
import { getPosVendorFromStore } from 'hooks/menu/get-pos-vendor-from-store';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { isEntryAvailable } from 'state/unified-menu/utils';
import { isDummyItem } from 'utils/cart/is-dummy-item';
import { CartEntryType } from 'utils/cart/types';
import { PosVendors } from 'utils/vendor-config';

import { itemIsAvailable, itemOptionModifierPluIsAvailable } from './menu-availability';

// This "availability" is actually very different from availability of IItem and friends
export const cartEntryIsAvailable = (
  cartEntry: ILegacyCartEntry,
  vendor: PosVendors | null,
  prices: IPrices,
  item?: ILegacyCartEntry
): boolean => {
  const isAvailable = (entry: ILegacyCartEntry) => {
    switch (entry.type) {
      case CartEntryType.combo:
        // we don't use comboIsAvailable here
        return itemIsAvailable(entry, vendor, prices);
      case CartEntryType.item:
        return itemIsAvailable(entry, vendor, prices);
      case CartEntryType.itemOptionModifier:
        // we only really need to know if the plu is available
        if (!item || !vendor) {
          return false;
        }
        return itemOptionModifierPluIsAvailable({
          item,
          itemOptionModifier: entry,
          vendor,
          prices,
        });
      case CartEntryType.comboSlot:
      case CartEntryType.itemOption:
        return true;
      case CartEntryType.offerCombo:
        return itemIsAvailable(entry, vendor, prices);
      case CartEntryType.offerItem:
        return itemIsAvailable(entry, vendor, prices);
      default:
        return false; // this shouldn't happen
    }
  };

  // hacking in a way to remember what the associated IItem is at every step
  const i = cartEntry.type === CartEntryType.item ? cartEntry : item;

  return (
    isAvailable(cartEntry) &&
    (cartEntry.children || []).every(entry => cartEntryIsAvailable(entry, vendor, prices, i))
  );
};

export const getUnavailableCartEntries = (
  cartEntries: ILegacyCartEntry[],
  restaurant: IRestaurant,
  prices: IPrices,
  serviceMode?: ServiceMode
) => {
  const vendor = getPosVendorFromStore(restaurant, serviceMode);
  const unavailableEntries = cartEntries.filter(
    (cartEntry: ILegacyCartEntry) => !cartEntryIsAvailable(cartEntry, vendor, prices)
  );

  return unavailableEntries;
};

export function isCartEntryAvailableOneMenu(
  cartEntry: ILegacyCartEntry,
  menu: IndexedMenu<ILocalizedMenuEntry>,
  posServiceMode: PosServiceMode,
  dayPartIds?: string[]
): boolean {
  if (
    [
      CartEntryType.combo,
      CartEntryType.item,
      CartEntryType.offerCombo,
      CartEntryType.offerItem,
      CartEntryType.itemOptionModifier,
    ].includes(cartEntry.type) &&
    !isDummyItem(cartEntry)
  ) {
    const menuEntry = menu.find(cartEntry._id);
    if (!menuEntry || !isEntryAvailable(menuEntry, posServiceMode, dayPartIds)) {
      return false;
    }
  }

  return cartEntry.children.every(child =>
    isCartEntryAvailableOneMenu(child, menu, posServiceMode, dayPartIds)
  );
}
